/**
 * Door-plaque QR export — tasks/checkout-plaque-artwork-generator.md (D73–D82).
 *
 * We generate the code; the designer builds the plaque around it. This module turns the QR that
 * `qrcode.react` renders on screen into a file a print shop can actually place, and it exists as a
 * separate module because three of its four rules are print rules that a component should not be
 * carrying:
 *
 *  1. The file is sized in MILLIMETRES, not pixels. An SVG whose width is "512" means nothing to a
 *     designer; one whose width is "49mm" drops into a template at the specified size with no
 *     scaling decision to get wrong — and scaling is precisely how the quiet zone and the module
 *     size fall out of specification.
 *  2. The physical size is DERIVED from the symbol, never hardcoded. Today's 95-character URL
 *     produces a 41 × 41 symbol which, plus the 4-module quiet zone, is 49 modules across → 49 mm at
 *     the 1 mm/module floor. If the URL ever changes length the symbol changes with it, and this
 *     arithmetic keeps the module size at the floor instead of silently shrinking it.
 *  3. Nothing readable in the file may carry the token (D78). The code encodes the URL — that is its
 *     job — but a `<title>` or `<desc>` element carrying the encoded text would hand the designer a
 *     permanent apartment credential as plain text, which is the one thing the whole
 *     generate-it-ourselves decision exists to prevent. `qrcode.react` only writes a title when
 *     asked, and we never ask; this strips it anyway, because the cost of being wrong is a
 *     credential in someone's inbox.
 */

/**
 * Error correction M. Not the library default (L), and deliberately not H: we obscure no part of
 * the code — no logo, no stylised modules — so H would spend capacity on damage we never inflict,
 * and push the symbol to a larger version for nothing. Q only if lamination testing later says so.
 */
export const QR_ERROR_CORRECTION = 'M';

/**
 * Quiet zone in modules. The QR specification requires 4; `qrcode.react` defaults to 0. Setting it
 * is not optional — a code whose quiet zone is crowded by a border or a logo is a code that fails
 * in a dim corridor, and the plaque around it is dark navy.
 */
export const QR_QUIET_ZONE_MODULES = 4;

/**
 * Millimetres per module, the production floor. Below roughly 1 mm a dense symbol starts failing
 * for a guest holding an older phone at an awkward angle — which is the only condition this code is
 * ever read under.
 */
const MM_PER_MODULE = 1;

/**
 * Read the module count back off the rendered symbol rather than assuming a QR version. The
 * viewBox `qrcode.react` writes is `0 0 N N`, where N already includes the quiet zone.
 */
const cellsAcross = (svg) => {
  const parts = (svg.getAttribute('viewBox') || '').trim().split(/\s+/);
  const n = Number(parts[3]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Serialise a rendered QR into a standalone, print-ready SVG document.
 *
 * @param {SVGSVGElement} svg the node `qrcode.react` rendered
 * @returns {string|null} the file's contents, or null if the symbol could not be measured
 */
export const toPrintableSvg = (svg) => {
  if (!svg) return null;

  const cells = cellsAcross(svg);
  if (!cells) return null;

  const copy = svg.cloneNode(true);

  // D78 — nothing readable carries the token.
  copy.querySelectorAll('title, desc, metadata').forEach((node) => node.remove());
  copy.removeAttribute('aria-label');

  // Required for a standalone file. React does not add it to inline SVG, and design tools reject a
  // .svg without it — the file would look fine in a browser and fail to open in Illustrator.
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const mm = `${cells * MM_PER_MODULE}mm`;
  copy.setAttribute('width', mm);
  copy.setAttribute('height', mm);
  copy.removeAttribute('style');

  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(copy)}\n`;
};

/**
 * Hand the file to the operator. Named for the apartment (D78) so the pairing the designer relies on
 * is the filename itself — there is no separate list to keep in step.
 */
export const downloadSvg = (filename, contents) => {
  const url = URL.createObjectURL(new Blob([contents], { type: 'image/svg+xml;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
